import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createInvestment, createLicence, generateControlNumber, gePayments, getLicenceApprovalStatus,
    getLicenceDetails, getLicences, getLicenseCategories, getLocations, paymentControlNumber,
    resubmitLicence, updateLicence,
    type ControlNumberInput, type LicenceUpdatePayload, type ZanMalipoResult,
} from "../services/applications-services";
import { TApiResponse, TErrorMessage, TPager, TSuccess } from "@/types";
import toast from "react-hot-toast";
export enum PaymentCategory {
    RESIDENCY = "PAYMENT FOR RESIDENCY",
    RESIDENCYFEE = "RESIDENCY FEE",
    LICENCE = "PAYMENT FOR LICENCE",
    LICENCEFEE = "LICENCE FEE",
    All = "All Categories",
    COVERLATER = "COVER LATER FEE",
    INSPECTIONFEE = "INSPECTION FEE"

}
export interface PaymentParams {
    controlNumber: string;
    amount: string;
    paymentCategory: PaymentCategory;
}

export function useGetLicenseCategories() {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: ["licences-categories"],
        queryFn: () => getLicenseCategories(),
    });

    return { isLoading, isFetching, licence_categories: data }
}


export function useCreateLicence(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn:(data:any)=> createLicence(data),
        onSuccess: (res) => {
            toast.success("successful created");

            // Without this the licence lists stay on their cached page after a submission,
            // so a new application only appears after a manual reload.
            //
            // refetchType "all" is the part that matters: invalidateQueries defaults to
            // "active", and the lists are unmounted at this moment — the applicant is still
            // on the submission form, which lives outside the applications layout. The
            // default therefore only flagged them stale and refetched nothing, leaving the
            // previous page of results to be re-displayed on the way back.
            queryClient.invalidateQueries({ queryKey: ["licences"], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["licences-details"], refetchType: "all" });

            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useCreateInvestment(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn:(data:any)=> createInvestment(data),
        onSuccess: (res) => {
            toast.success("successful created");

            // Same reason as useCreateLicence above — the investment list is unmounted while
            // the form is open, so an "active"-only invalidation never refetches it.
            queryClient.invalidateQueries({ queryKey: ["QUERY_KEY"], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["investment-details"], refetchType: "all" });

            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

// export function useCreatePaymentControlNumber(
//     onSuccess?: TSuccess<TApiResponse<null>>
// ) {
//
//     return useMutation({
//         mutationFn: () => generatPaymentControlNumber(),
//         mutationKey: ["generate-control-number"],
//         onSuccess: (res) => {
//             // toast.success("successful created");
//
//             onSuccess?.(res);
//         },
//         onError: (error: TErrorMessage) => {
//             toast.error(error.response?.data.message ?? "");
//         },
//     })
// }

// Updated hook with payment amount
export interface PaymentParams {
    controlNumber: string;
    amount: string;
}
//
// export function useCreatePaymentForControlNumber(
//     onSuccess?: TSuccess<TApiResponse<null>>
// ) {
//     return useMutation({
//         mutationFn: (params: PaymentParams) => paymentControlNumber(params),
//         mutationKey: ["payment-control-number"],
//         onSuccess: (res) => {
//             onSuccess?.(res);
//         },
//         onError: (error: TErrorMessage) => {
//             toast.error(error.response?.data.message ?? "");
//         },
//     })
// }

export function usePayment(
    onSuccess?: TSuccess<TApiResponse<ZanMalipoResult>>,
    onPaymentSuccess?: () => void
) {
    const { data, mutate: generateControlNumberMutate, isPending: isGenerating } = useMutation({
        mutationFn: (input: ControlNumberInput) => generateControlNumber(input),
        mutationKey: ["generate-control-number"],
        onSuccess: (res) => {
            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "Failed to generate control number");
        },
    });

    const { mutate: processPayment, isPending: isProcessing, isSuccess } = useMutation({
        mutationFn: ({ controlNumber, amount, paymentCategory }: PaymentParams) =>
            paymentControlNumber({ controlNumber, amount, paymentCategory }),
        mutationKey: ["process-payment"],
        onSuccess: () => {
            toast.success("Payment processed successfully");
            onPaymentSuccess?.();
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "Payment failed");
        },
    });

    return {
        controlNumber: data?.results?.controlNumber,
        generateControlNumber: generateControlNumberMutate,
        isGenerating,
        processPayment,
        isProcessing,
        isSuccess,
    };
}


export function useGetLicences(options: {
    search?: string,
    pager?: TPager,
    filter?: Record<string, unknown>
})  {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: ["licences",options.search, {...options.pager, ...options.filter}],
        queryFn: () => getLicences(options.search, options.pager, options.filter),
        // Always go to the network when the list is re-entered. A submission made
        // elsewhere in the app must not be able to leave a stale page on screen.
        refetchOnMount: "always",
    });

    return { isLoading, isFetching, licences: data }
}


export function useGetPayments(options: {
    search?: string,
    pager?: TPager,
    filter?: Record<string, unknown>
})  {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: ["get-payments",options.search, {...options.pager, ...options.filter}],
        queryFn: () => gePayments(options.search, options.pager, options.filter),
    });

    return { isLoading, isFetching, payments: data }
}

export function useGetLocation(options: {
    search?: string,
    pager?: TPager,
    filter?: Record<string, unknown>
})  {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: ["locations-details",options.search, {...options.pager, ...options.filter}],
        queryFn: () => getLocations(options.search, options.pager, options.filter),
    });

    return { isLoading, isFetching, zones: data }
}




export  function useGetLicenseDetails(applicationId: string) {
    const { isLoading, isFetching, data,isStale } = useQuery({
        queryKey: ["licences-details", applicationId],
        queryFn: () => getLicenceDetails(applicationId),
    });

    return { isLoading, isFetching, licence_details: data,isStale }
}

// A payment is "settled" once its status is terminal — no more polling needed.
const TERMINAL_PAYMENT_STATUS = /paid|success|complete|failed|expired|cancel/i;

function isTerminalPaymentStatus(status?: unknown): boolean {
    return typeof status === "string" && TERMINAL_PAYMENT_STATUS.test(status);
}

function firstPaymentFrom(data: unknown): any {
    const paymentData = (data as any)?.results || data;
    const list = Array.isArray(paymentData) ? paymentData : (paymentData ? [paymentData] : []);
    return list[0] || null;
}

export function useGetPaymentByApplicationId(applicationId: string) {
    const { isLoading, isFetching, data, refetch } = useQuery({
        queryKey: ["payment-by-application", applicationId],
        queryFn: () => gePayments(undefined, undefined, {
            // Try different possible filter field names - API might use either
            applicationId: applicationId,
            application_id: applicationId
        }),
        enabled: !!applicationId, // Only fetch if applicationId exists
        // Auto-refresh while the payment is non-terminal so a bill paid later
        // reflects without a manual refresh; stop once it settles.
        refetchInterval: (query) => {
            const payment = firstPaymentFrom(query.state.data);
            const status = payment?.status ?? payment?.paymentStatus;
            return isTerminalPaymentStatus(status) ? false : 15_000;
        },
    });

    // Handle different response structures
    const paymentData = data?.results  || data;
    const paymentList = Array.isArray(paymentData) ? paymentData : (paymentData ? [paymentData] : []);

    return {
        isLoading,
        isFetching,
        payment: paymentList[0] || null,
        payments: paymentList,
        refetch
    }
}

/**
 * Approval workflow status for a licence application — legal review, document
 * review and the final decision, each with the reviewer's own comment.
 *
 * The endpoint 404s/errors for applications that were never pushed into the
 * approval workflow, so failures are surfaced (not toasted) and the panel
 * renders an honest empty state instead of retrying.
 */
export function useGetLicenceApprovalStatus(applicationId: string) {
    const { isLoading, isFetching, isError, data } = useQuery({
        queryKey: ["licence-approval-status", applicationId],
        queryFn: () => getLicenceApprovalStatus(applicationId),
        enabled: !!applicationId,
        retry: false,
    });

    return { isLoading, isFetching, isError, approval_status: data }
}

/**
 * Resubmit a licence application a reviewer returned for correction.
 *
 * The backend answers 409 CONFLICT when the application is not actually in
 * RETURNED_FOR_CORRECTION, with a message naming the state it is in — that
 * message is surfaced rather than a generic failure.
 *
 * On success the detail, approval-status and list queries are invalidated so
 * the new stage shows up straight away.
 */
export function useResubmitLicence(applicationId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => resubmitLicence(applicationId),
        mutationKey: ["resubmit-licence", applicationId],
        onSuccess: (res) => {
            toast.success(res.message ?? "Application resubmitted successfully");
            queryClient.invalidateQueries({ queryKey: ["licences-details", applicationId], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["licence-approval-status", applicationId], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["licences"], refetchType: "all" });
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "Failed to resubmit application");
        },
    })
}

/**
 * Save an edit to a licence application the applicant already submitted.
 *
 * The API's own message is what gets shown, because both refusals are things
 * the applicant can understand and act on: 403 "You can only edit your own
 * applications" when the audited creator is someone else, and 409 naming the
 * terminal state ("...because it is approved.") once it can no longer change.
 *
 * On success the detail and list queries are invalidated so the edit is visible
 * straight away.
 */
export function useUpdateLicence(
    applicationId: string,
    onSuccess?: TSuccess<TApiResponse<any>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: LicenceUpdatePayload) => updateLicence(applicationId, data),
        mutationKey: ["update-licence", applicationId],
        onSuccess: (res) => {
            toast.success(res.message ?? "Application updated successfully");
            queryClient.invalidateQueries({ queryKey: ["licences-details", applicationId], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["licences"], refetchType: "all" });
            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "Failed to update application");
        },
    })
}
